'use client';

import { createClient } from '@/lib/supabase/client';

export interface DropdownOption {
  id: string;
  category: string;
  value: string;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
}

function mapOption(row: any): DropdownOption {
  return {
    id: row.id,
    category: row.category,
    value: row.value,
    sortOrder: row.sort_order,
    isActive: row.is_active,
    createdAt: row.created_at,
  };
}

export const dropdownService = {
  async getByCategory(category: string): Promise<string[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('dropdown_options')
        .select('value')
        .eq('category', category)
        .eq('is_active', true)
        .order('sort_order', { ascending: true });
      if (error) return [];
      return (data ?? []).map((r: any) => r.value);
    } catch {
      return [];
    }
  },

  async getAllByCategory(category: string): Promise<DropdownOption[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('dropdown_options')
        .select('*')
        .eq('category', category)
        .order('sort_order', { ascending: true });
      if (error) return [];
      return (data ?? []).map(mapOption);
    } catch {
      return [];
    }
  },

  async getAllCategories(): Promise<Record<string, DropdownOption[]>> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('dropdown_options')
        .select('*')
        .order('category', { ascending: true })
        .order('sort_order', { ascending: true });
      if (error) return {};
      const grouped: Record<string, DropdownOption[]> = {};
      for (const row of data ?? []) {
        const opt = mapOption(row);
        if (!grouped[opt.category]) grouped[opt.category] = [];
        grouped[opt.category].push(opt);
      }
      return grouped;
    } catch {
      return {};
    }
  },

  async create(payload: {
    category: string;
    value: string;
    sortOrder?: number;
    createdBy: string;
  }): Promise<DropdownOption | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('dropdown_options')
        .insert({
          category: payload.category,
          value: payload.value.trim(),
          sort_order: payload.sortOrder ?? 99,
          created_by: payload.createdBy,
        })
        .select('*')
        .single();
      if (error) return null;
      return mapOption(data);
    } catch {
      return null;
    }
  },

  async update(id: string, payload: { value?: string; sortOrder?: number; isActive?: boolean }): Promise<boolean> {
    const supabase = createClient();
    try {
      const updates: any = { updated_at: new Date().toISOString() };
      if (payload.value !== undefined) updates.value = payload.value.trim();
      if (payload.sortOrder !== undefined) updates.sort_order = payload.sortOrder;
      if (payload.isActive !== undefined) updates.is_active = payload.isActive;
      const { error } = await supabase
        .from('dropdown_options')
        .update(updates)
        .eq('id', id);
      return !error;
    } catch {
      return false;
    }
  },

  async delete(id: string): Promise<boolean> {
    const supabase = createClient();
    try {
      const { error } = await supabase
        .from('dropdown_options')
        .delete()
        .eq('id', id);
      return !error;
    } catch {
      return false;
    }
  },
};

// ─── USER ROLE MANAGEMENT ─────────────────────────────────────────────────────

export interface AppUser {
  id: string;
  email: string;
  fullName: string;
  appRole: 'admin' | 'user';
  farmRole: string;
  isActive: boolean;
  createdAt: string;
}

export const userRoleService = {
  async getAllUsers(): Promise<AppUser[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id, email, full_name, app_role, farm_role, is_active, created_at')
        .order('created_at', { ascending: true });
      if (error) return [];
      return (data ?? []).map((row: any) => ({
        id: row.id,
        email: row.email,
        fullName: row.full_name,
        appRole: row.app_role ?? 'user',
        farmRole: row.farm_role ?? 'farm-worker',
        isActive: row.is_active,
        createdAt: row.created_at,
      }));
    } catch {
      return [];
    }
  },

  async setRole(userId: string, role: 'admin' | 'user'): Promise<boolean> {
    const supabase = createClient();
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({ app_role: role, updated_at: new Date().toISOString() })
        .eq('id', userId);
      return !error;
    } catch {
      return false;
    }
  },

  async updateUser(userId: string, payload: { fullName?: string; farmRole?: string }): Promise<boolean> {
    const supabase = createClient();
    try {
      const updates: any = { updated_at: new Date().toISOString() };
      if (payload.fullName !== undefined) updates.full_name = payload.fullName.trim();
      if (payload.farmRole !== undefined) updates.farm_role = payload.farmRole.trim();
      const { error } = await supabase
        .from('user_profiles')
        .update(updates)
        .eq('id', userId);
      return !error;
    } catch {
      return false;
    }
  },
};
